/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  createFormulaModel,
  Formula,
  FormulaElementGroup,
  getFormulaParams,
} from '@gh/builder-shared-data';
import { Dictionary } from '@gh/common-shared';
import { isMessage, isMessageBasket } from '@gh/core-messages';
import { TranslationService } from '@gh/core-mls';
import { WaitingService } from '@gh/core-ui';
import { isNotNil, isObservable, wrapInObservable } from '@gh/core-util';
import { EMPTY$, throwError$ } from '@gh/rx/index';
import { catchError$, switchMap$ } from '@gh/rx/operators';
import { first } from 'lodash';
import { Observable, Subject, Subscription } from 'rxjs';
import { FormulaContext } from '../../formula-context';
import { getReadableFormulaExpression } from '../../formula.utils';
import { charStream, FormulaParser } from '../../parser';
import { FormulaValidationResult } from '../../parser/formula-errors';
import { DBFormulaRewriteStrategy } from '../../parser/formula-rewrite-strategy';

const FORMULA_REWRITE_STRATEGY = new DBFormulaRewriteStrategy();

export interface FormulaTestEvent {
  formula: Formula;

  setValue(ob: Observable<string> | string): void;

  setError(text: string): void;
}

@Component({
  standalone: false,
  selector: 'gh-formula-builder',
  templateUrl: 'formula-builder.component.html',
  styleUrls: ['formula-builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: FormulaBuilderComponent, multi: true }],
})
export class FormulaBuilderComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @Input() context: FormulaContext;
  @Input() selectionIdentity?: unknown;

  @Output() change = new EventEmitter<Formula>();
  @Output('test') testEvents = new EventEmitter<FormulaTestEvent>();

  value: string = '';
  formula: Formula;
  selectedGroup: any;
  selectedElement?: any;

  elements: string[];

  validation?: FormulaValidationResult;
  disabled: boolean = false;
  testPending = false;
  calculatedValue?: string;

  @ViewChild('editor', { static: false }) _editor: ElementRef;

  private parser: FormulaParser;
  private _onChange?: (value: unknown) => void;
  private onTouched?: () => void;
  private _live: boolean = false;

  private value$$ = new Subject<Observable<Maybe<string>>>();
  private testError?: string;
  private contextSubscription: Subscription;

  constructor(private translationService: TranslationService,
              private waitings: WaitingService,
              private changeDetectorRef: ChangeDetectorRef) {
    this.setCalculatedValue = this.setCalculatedValue.bind(this);
    this.setTestError = this.setTestError.bind(this);
  }

  /**
   * Turns on live validation. In this mode formula is validated on every keypress.
   */
  @Input()
  set live(live: boolean) {
    this._live = live;

    if (live) {
      this.test();
    }
  }

  get live(): boolean {
    return this._live;
  }

  get groups(): Dictionary<any> {
    return this.context.groups;
  }

  get editor(): HTMLTextAreaElement {
    return this._editor.nativeElement;
  }

  get valid(): boolean {
    return this.validation && this.validation.valid || false;
  }

  get invalid(): boolean {
    return this.validation && !this.validation.valid || false;
  }

  get errorMessage(): string {
    const { testError, validation, context } = this;
    if (testError) {
      return testError;
    }

    const errorId = validation && validation.errorId;
    return errorId && !!context?.errorBundle?.get ? context.errorBundle.get(errorId) : '';
  }

  get hasCalculatedValue(): boolean {
    return isNotNil(this.calculatedValue);
  }

  get hasTestError(): boolean {
    return isNotNil(this.testError);
  }

  ngOnInit(): void {
    this.parser = new FormulaParser(this.context.nameResolver);

    this.selectGroup(FormulaElementGroup.TakeOffParams);

    this.value$$.pipe(
      switchMap$((value$) =>
        value$.pipe(
          this.waitings.operator(),
          catchError$((error) => {
            if (isMessageBasket(error)) {
              const message = error.at(0);
              this.setTestError(this.translationService.get(message.key, message.params));
              return EMPTY$;
            } else if (isMessage(error)) {
              this.setTestError(this.translationService.get(error.key, error.params));
              return EMPTY$;
            } else {
              return throwError$(error);
            }
          }))))
      .subscribe((value) => {
        this.calculatedValue = value;
        this.testPending = false;
        this.changeDetectorRef.markForCheck();
      });

    this.contextSubscription = this.context.stateChanges.subscribe(() => {
      this.elements = this.context.getSnippets(this.selectedGroup);
      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const sel = changes['selectionIdentity'];
    if (!sel || sel.firstChange) {
      return;
    }
    if (Object.is(sel.previousValue, sel.currentValue)) {
      return;
    }
    queueMicrotask(() => {
      this.clearTestDisplayState();
    });
  }

  ngOnDestroy(): void {
    this.value$$.complete();
    if (this.contextSubscription) {
      this.contextSubscription.unsubscribe();
    }
  }

  test(): void {
    const validation = this.parser.validate(charStream(this.value));

    if (!validation.valid) {
      const position = <number>validation.position;
      this.editor.setSelectionRange(position, position);
      this.editor.focus();
    }

    this.validation = validation;

    this.changeDetectorRef.markForCheck();

    this.setCalculatedValue(void 0);
    this.setTestError(void 0);

    if (validation.valid) {
      this.testEvents.emit({
        formula: this.formula,
        setValue: this.setCalculatedValue,
        setError: this.setTestError,
      });
    }
  }

  onDropElement(event: CdkDragDrop<string>): void {
    if (!this.disabled) {
      this.addElement(event.item.data);
    }
  }

  onInput(value: string): void {
    this.setValue(value);
  }

  onBlur(): void {
    if (this.onTouched) {
      this.onTouched();
    }
  }

  onTest(): void {
    this.test();
  }

  onClear(): void {
    this.setValue('');
    this.validation = void 0;
  }

  onSelectGroup(groupId: any): void {
    this.selectGroup(groupId);
  }

  onSelectElement(elementId: any): void {
    this.selectedElement = elementId;

    this.changeDetectorRef.markForCheck();
  }

  onAddElement(element: string): void {
    if (!this.disabled) {
      this.addElement(element);
    }
  }

  onAddSelectedElement(): void {
    if (this.selectedElement) {
      this.addElement(this.selectedElement);
      this.selectedElement = void 0;
    }
  }

  writeValue(value?: Formula): void {
    const { context } = this;
    this.value = getReadableFormulaExpression(context, value);
    this.updateFormula();

    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;

    this.changeDetectorRef.markForCheck();
  }

  getElementTranslation(key: string): string {
    const translationKey = `lbl_formula_element:${key}`;
    const raw = this.translationService.getRaw(translationKey);

    if (raw) {
      return this.translationService.get(translationKey);
    } else {
      return '';
    }
  }

  private setValue(value: string): void {
    this.value = value;
    this.validation = void 0;
    this.updateFormula();
    if (this._onChange) {
      this._onChange(this.formula);
    }

    this.change.emit(this.formula);

    this.changeDetectorRef.markForCheck();
  }

  private selectGroup(groupId: FormulaElementGroup): void {
    const elements = this.context.getSnippets(groupId);
    this.selectedGroup = groupId;
    this.selectedElement = first(elements);
    this.elements = elements;

    this.changeDetectorRef.markForCheck();
  }

  private addElement(element: string): void {
    const { editor } = this;

    this.setValue(this.value + element);
    editor.setSelectionRange(editor.value.length, editor.value.length);
    editor.focus();
  }

  private updateFormula(): void {
    const { value } = this;
    const rewriteResult = this.parser.rewrite(charStream(value), FORMULA_REWRITE_STRATEGY);
    this.formula = rewriteResult.valid ?
       
      createFormulaModel(rewriteResult.rewritten!, getFormulaParams(rewriteResult.rewritten!), true) :
      createFormulaModel(value, [], false);

    if (this.live) {
      this.validation = rewriteResult;
    }

    this.setCalculatedValue(void 0);
    this.setTestError(void 0);
  }

  private setCalculatedValue(value: Maybe<Observable<string> | string>): void {
    this.testPending = false;

    if (isObservable(value)) {
      this.setTestPending(true);
      this.value$$.next(value);
    } else {
      this.value$$.next(wrapInObservable(value));
    }
  }

  private setTestError(error?: string): void {
    this.testError = error;
    this.testPending = false;
    this.changeDetectorRef.markForCheck();
  }

  private setTestPending(pending: boolean): void {
    this.testPending = pending;
  }

  private clearTestDisplayState(): void {
    this.calculatedValue = void 0;
    this.testError = void 0;
    this.testPending = false;
    this.validation = void 0;
    this.changeDetectorRef.markForCheck();
  }
}
